
/**
 * Scaling strategies for the fraud detection service
 * This file contains utilities for implementing horizontal scaling,
 * load balancing, and service partitioning
 */

import { CACHE_DURATIONS, localStorageCache, generateCacheKey } from './cacheUtils';
import { authRateLimiter } from './rateLimiter';
import { logInfo, logError } from './logger';
import { loadBalancer, getServiceUrl, releaseConnection } from './loadBalancer';

// Shard keys for distributed operations
export const SHARD_TYPES = {
  USER: 'user',
  ANALYSIS: 'analysis',
  REGION: 'region',
  SERVICE: 'service'
};

// Service partitioning
export interface ServicePartition {
  id: string;
  name: string;
  weight: number; // Load balancing weight (1-100)
  status: 'active' | 'draining' | 'inactive';
  region: string;
  capabilities: string[]; // What this partition can handle
}

// Initial service partitions configuration (would be fetched from configuration service in a real system)
const SERVICE_PARTITIONS: ServicePartition[] = [
  {
    id: 'primary',
    name: 'Primary Analysis Service',
    weight: 100,
    status: 'active',
    region: 'us-east-1',
    capabilities: ['text_analysis', 'image_analysis', 'document_analysis']
  },
  {
    id: 'secondary',
    name: 'Secondary Analysis Service',
    weight: 50,
    status: 'active',
    region: 'us-west-1',
    capabilities: ['text_analysis', 'image_analysis', 'video_analysis', 'voice_analysis']
  },
  {
    id: 'specialized',
    name: 'Specialized Analysis Service',
    weight: 30,
    status: 'active',
    region: 'eu-central-1',
    capabilities: ['proxy_analysis', 'video_analysis', 'voice_analysis']
  }
];

// Get available service partitions for a specific analysis type
export const getServicePartitionsForAnalysis = (analysisType: string): ServicePartition[] => {
  const cacheKey = generateCacheKey(`partitions:${analysisType}`, {});
  const cached = localStorageCache.get<ServicePartition[]>(cacheKey);
  
  if (cached) {
    return cached;
  }
  
  // Filter partitions that support the requested analysis type
  const partitions = SERVICE_PARTITIONS.filter(
    p => p.status === 'active' && p.capabilities.includes(analysisType)
  );
  
  // Cache result for a short period
  localStorageCache.set(cacheKey, partitions, CACHE_DURATIONS.SHORT);
  
  return partitions;
};

// Weighted round-robin load balancer using our new load balancer module
export const selectServicePartition = (analysisType: string, userId?: string): ServicePartition | null => {
  const partitions = getServicePartitionsForAnalysis(analysisType);
  
  if (!partitions || partitions.length === 0) {
    logError('Scaling', `No service partitions available for ${analysisType}`);
    return null;
  }
  
  // Use load balancer to get appropriate instance based on configured strategy
  const instanceId = loadBalancer.getNextInstance(userId)?.id;
  const partition = partitions.find(p => p.id === instanceId) || partitions[0];
  
  return partition;
};

// Simple string hash function for consistent routing
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

// Generate a shard key for distributed operations
export const generateShardKey = (type: string, id: string): string => {
  return `${type}:${id}`;
};

// Backoff strategy for retries
export const calculateBackoff = (attempt: number, baseMs = 100, maxMs = 10000): number => {
  const backoff = Math.min(maxMs, baseMs * Math.pow(2, attempt));
  // Add jitter to prevent thundering herd
  return backoff + (Math.random() * backoff * 0.1);
};

// Circuit breaker pattern for external service calls
export class CircuitBreaker {
  private failures: number = 0;
  private lastFailureTime: number = 0;
  private state: 'closed' | 'open' | 'half-open' = 'closed';
  
  constructor(
    private readonly failureThreshold: number = 3,
    private readonly resetTimeout: number = 30000
  ) {}
  
  public canRequest(): boolean {
    const now = Date.now();
    
    // If circuit is open, check if we should try half-open
    if (this.state === 'open') {
      if (now - this.lastFailureTime > this.resetTimeout) {
        this.state = 'half-open';
        logInfo('Circuit breaker transitioning to half-open state', 'circuit-breaker');
        return true;
      }
      return false;
    }
    
    return true;
  }
  
  public recordSuccess(): void {
    if (this.state === 'half-open') {
      this.state = 'closed';
      this.failures = 0;
      logInfo('Circuit breaker reset to closed state', 'circuit-breaker');
    }
  }
  
  public recordFailure(): boolean {
    this.failures += 1;
    this.lastFailureTime = Date.now();
    
    if (this.state === 'closed' && this.failures >= this.failureThreshold) {
      this.state = 'open';
      logInfo(`Circuit breaker tripped to open state after ${this.failures} failures`, 'circuit-breaker');
      return false;
    }
    
    if (this.state === 'half-open') {
      this.state = 'open';
      logInfo('Circuit breaker returned to open state after failure in half-open state', 'circuit-breaker');
      return false;
    }
    
    return true;
  }
  
  public getState(): string {
    return this.state;
  }
}

// Global circuit breakers for services
export const serviceCircuitBreakers: Record<string, CircuitBreaker> = {
  'text_analysis': new CircuitBreaker(),
  'image_analysis': new CircuitBreaker(),
  'video_analysis': new CircuitBreaker(),
  'voice_analysis': new CircuitBreaker(),
  'document_analysis': new CircuitBreaker(),
  'proxy_analysis': new CircuitBreaker()
};

// Check if we can make a service request accounting for rate limits and circuit breakers
export const canMakeServiceRequest = (
  userId: string, 
  serviceType: string,
  operation: string
): boolean => {
  // Check rate limits first
  if (!authRateLimiter.check(`${userId}:${operation}`).allowed) {
    logInfo(`Rate limit reached for user ${userId} on ${operation}`, 'rate-limiter');
    return false;
  }
  
  // Then check circuit breaker
  const circuitBreaker = serviceCircuitBreakers[serviceType];
  if (circuitBreaker && !circuitBreaker.canRequest()) {
    logInfo(`Circuit open for ${serviceType}, request blocked`, 'circuit-breaker');
    return false;
  }
  
  return true;
};

// Track success or failure for a service request
export const trackServiceResult = (
  serviceType: string,
  success: boolean
): void => {
  const circuitBreaker = serviceCircuitBreakers[serviceType];
  if (!circuitBreaker) return;
  
  if (success) {
    circuitBreaker.recordSuccess();
  } else {
    circuitBreaker.recordFailure();
  }
};

// Export functions from load balancer
export { getServiceUrl, releaseConnection };
